import { useCallback, useLayoutEffect, useRef } from 'react';
import { useOnboardingStore } from '@stores/onboardingStore';
import {
  OnboardingFlowId,
  EventGoingWelcomeVariant,
  type FlowVariantMap,
  type OnboardingAction,
} from '@config/onboarding-flows';
import { featureFlags } from '@config/featureFlags';
import { isCompleted } from '@utils/onboardingProgress';

interface TriggerOnboardingFlowParams<F extends OnboardingFlowId> {
  flowId: F;
  variant: FlowVariantMap[F];
  actions?: Partial<Record<OnboardingAction, () => void>>;
  contextId?: string;
}

/**
 * Imperative guard sequence for opening an onboarding flow — the single source of truth shared by
 * `useOnboarding.trigger` (the reactive path) and non-hook callers like `openFirstGoingWelcome`
 * (`useEventGoingSave`). Reads completion at CALL time, not render time, so it always sees a fresh
 * `markCompleted` (which may have fired elsewhere); then opens the store, which rejects an
 * empty-steps variant itself. Drives the store via `getState()` so a caller adds no reactive
 * subscription — the T-13 invariant `useEventGoingSave` depends on (per-cell live rows must not
 * re-render outside their own 30s tick). `isCompleted` honors each flow's `completionScope`, so
 * both callers inherit the flow-vs-variant scope automatically.
 */
export function triggerOnboardingFlow<F extends OnboardingFlowId>(
  params: TriggerOnboardingFlowParams<F>,
): void {
  const { flowId, variant, actions, contextId } = params;

  if (isCompleted(flowId, variant, contextId)) return;

  useOnboardingStore.getState().openOnboarding({ flowId, variant, actions, contextId });
}

/**
 * Open the first-Going welcome drawer once ever per device, from a going=ON success only.
 *
 * Delegates to `triggerOnboardingFlow` (the shared imperative guard sequence): completion check →
 * empty-steps guard → open, all via `getState()`. That keeps this path free of any reactive store
 * subscription — the T-13 invariant is that per-cell live rows never re-render outside their own 30s
 * tick, and a subscription here would break it for every cell at once. Completion is flow-scoped for
 * EventGoingWelcome (see onboarding-flows `completionScope`), so flipping `helpableAtEvent` after a
 * user has already seen the Default drawer does NOT replay the WithHelpable one — `triggerOnboardingFlow`
 * inherits that from `isCompleted`. `featureFlags.helpableAtEvent` still picks the variant whose copy
 * matches the surface that cohort has.
 *
 * No `actions` registered: the flow's final "Got it" step is label-only (no onNext), which
 * OnboardingCarousel routes straight to GlobalOnboarding's onComplete — instant close +
 * markCompleted + completed analytics, no pending-spinner state.
 */
export const openFirstGoingWelcome = (): void => {
  const variant = featureFlags.helpableAtEvent
    ? EventGoingWelcomeVariant.WithHelpable
    : EventGoingWelcomeVariant.Default;
  triggerOnboardingFlow({ flowId: OnboardingFlowId.EventGoingWelcome, variant });
};

interface UseOnboardingOptions<F extends OnboardingFlowId> {
  variant: FlowVariantMap[F];
  actions?: Partial<Record<OnboardingAction, () => void>>;
  contextId?: string;
}

export const useOnboarding = <F extends OnboardingFlowId>(
  flowId: F,
  options: UseOnboardingOptions<F>,
) => {
  const { variant, actions, contextId } = options;

  const completed = isCompleted(flowId, variant, contextId);

  const actionsRef = useRef(actions);
  useLayoutEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  const trigger = useCallback(() => {
    triggerOnboardingFlow({ flowId, variant, actions: actionsRef.current, contextId });
  }, [flowId, variant, contextId]);

  return { trigger, isCompleted: completed };
};
